import React, { useEffect, useState, useCallback } from 'react';
import { getDashboard, forceClockOut } from '../api';
import { getInitials, scoreColor, weekRange } from '../utils';
import { useAuth } from '../AuthContext';
import EmployeeProfile from '../components/EmployeeProfile';

export default function TeamView() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const { isAdmin } = useAuth();

  const load = useCallback(async () => {
    const { from, to } = weekRange();
    const res = await getDashboard({ from, to });
    setData(res);
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  if (!data) return <div className="empty">Loading...</div>;

  // On-break employees are still clocked in — show them with Active Now, not Offline.
  const active = data.employees.filter(e => e.is_active || e.on_break);
  const offline = data.employees.filter(e => !e.is_active && !e.on_break);
  const onForceClockOut = async (id) => { await forceClockOut(id); load(); };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Team View</h1>
        <span className="badge badge-green">{active.length} active</span>
      </div>

      {active.length > 0 && (
        <>
          <h2 style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--muted)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '.5px' }}>Active Now</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16, marginBottom: 24 }}>
            {active.map(emp => <EmployeeCard key={emp.employee.id} emp={emp} isAdmin={isAdmin} onForceClockOut={onForceClockOut} onOpen={setSelected} />)}
          </div>
        </>
      )}

      <h2 style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--muted)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '.5px' }}>Offline</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
        {offline.map(emp => <EmployeeCard key={emp.employee.id} emp={emp} onOpen={setSelected} />)}
      </div>

      {selected && <EmployeeProfile emp={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function EmployeeCard({ emp, isAdmin, onForceClockOut, onOpen }) {
  const [elapsed, setElapsed] = useState(0);
  const clockedIn = emp.is_active || emp.on_break; // on-break is still clocked in

  useEffect(() => {
    // Timer shows today's CUMULATIVE worked time (resumes across sessions), not
    // just the current session. Tick only while actively working; freeze the
    // running total on break or when offline.
    const base = emp.today_worked_seconds || 0;
    setElapsed(base);
    if (!emp.is_active) return;
    const t = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(t);
  }, [emp.today_worked_seconds, emp.is_active]);

  const h = String(Math.floor(elapsed / 3600)).padStart(2, '0');
  const m = String(Math.floor((elapsed % 3600) / 60)).padStart(2, '0');
  const s = String(elapsed % 60).padStart(2, '0');

  return (
    <div className="card" onClick={() => onOpen && onOpen(emp)}
      style={{ borderLeft: `3px solid ${emp.is_active ? 'var(--success)' : emp.on_break ? '#f59e0b' : 'var(--border)'}`, cursor: onOpen ? 'pointer' : 'default' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div className="employee-avatar" style={{ width: 44, height: 44, fontSize: '0.95rem', position: 'relative' }}>
          {getInitials(emp.employee.name)}
          <span style={{
            position: 'absolute', bottom: 0, right: 0, width: 12, height: 12,
            borderRadius: '50%', background: emp.is_active ? 'var(--success)' : emp.on_break ? '#f59e0b' : '#94a3b8',
            border: '2px solid white'
          }} />
        </div>
        <div>
          <div style={{ fontWeight: 600 }}>{emp.employee.name}</div>
          <div className="text-muted" style={{ fontSize: '0.8rem' }}>{emp.employee.department}</div>
        </div>
        <span className={`badge ${emp.is_active ? 'badge-green' : emp.on_break ? 'badge-orange' : 'badge-gray'}`} style={{ marginLeft: 'auto' }}>
          {emp.is_active ? 'Active' : emp.on_break ? 'On Break' : 'Offline'}
        </span>
      </div>

      {clockedIn && (
        <>
          <div style={{ marginBottom: 4, fontFamily: 'monospace', fontSize: '1.4rem', fontWeight: 700, color: emp.on_break ? '#b45309' : elapsed > 43200 ? '#ef4444' : 'var(--primary)' }}>
            {h}:{m}:{s}{emp.on_break ? ' ⏸' : ''}
          </div>
          {!emp.on_break && elapsed > 43200 && (
            <div style={{ fontSize: '0.75rem', color: '#ef4444', marginBottom: 8, fontWeight: 500 }}>
              ⚠️ Session over 12 hours — likely forgot to clock out
            </div>
          )}
          {isAdmin && (
            <button
              className="btn btn-danger btn-sm"
              style={{ marginBottom: 12, fontSize: '0.75rem' }}
              onClick={(ev) => { ev.stopPropagation(); if (window.confirm(`Force clock out ${emp.employee.name}?`)) onForceClockOut(emp.employee.id); }}>
              ⏹ Force Clock Out
            </button>
          )}
        </>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <div className="stat-label">This Week</div>
          <div style={{ fontWeight: 600 }}>{(emp.total_seconds / 3600).toFixed(1)}h</div>
        </div>
        <div>
          <div className="stat-label">Sessions</div>
          <div style={{ fontWeight: 600 }}>{emp.sessions}</div>
        </div>
      </div>

      <div style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: 4 }}>
          <span className="text-muted">Productivity</span>
          <span style={{ fontWeight: 600, color: scoreColor(emp.productivity_score) }}>{emp.productivity_score}%</span>
        </div>
        <div className="score-bar">
          <div className="score-fill" style={{ width: `${emp.productivity_score}%`, background: scoreColor(emp.productivity_score) }} />
        </div>
      </div>
    </div>
  );
}
